import { supabase } from '../lib/supabase';
import { OffersService } from './offersService';

export interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  conversation_id: string;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'document' | 'video' | 'offer';
  is_read: boolean;
  created_at: string;
  updated_at: string;
  offer_id?: string;
  product_id?: string;
  image_url?: string;
  image_path?: string;
  image_filename?: string;
  image_size?: number;
  image_mime_type?: string;
  image_width?: number;
  image_height?: number;
  image_alt_text?: string;
  document_url?: string;
  document_path?: string;
  document_filename?: string;
  document_size?: number;
  document_mime_type?: string;
  document_extension?: string;
  document_category?: string;
  video_url?: string;
  video_path?: string;
  video_filename?: string;
  video_size?: number;
  video_mime_type?: string;
  video_duration?: number;
  video_width?: number;
  video_height?: number;
  video_thumbnail_url?: string;
  video_thumbnail_path?: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    alias?: string;
  };
  receiver?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    alias?: string;
  };
}

export interface Conversation {
  id: string;
  user1_id: string;
  user2_id: string;
  last_message_id?: string;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  product_id?: string;
  offer_id?: string;
  subject?: string;
  active_users?: string[];
  last_message?: Message;
  other_user?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    alias?: string;
  };
  unread_count?: number;
  message_count?: number;
  product?: {
    id: string;
    name: string;
    price: number;
    images: string[];
  };
  offerValidation?: {
    canMakeOffer: boolean;
    reason?: string;
    productInfo?: {
      id: string;
      name: string;
      price: number;
      seller_id: string;
      is_active: boolean;
    };
  };
}

export class MessageService {
  // Lightweight local event bus to broadcast newly sent messages immediately
  private static localMessageListeners: Array<(message: Message) => void> = [];

  private static async getCanonicalInquirySubject(productId: string, fallback?: string): Promise<string> {
    const fb = String(fallback || '').trim();
    return fb || 'Inquiry';
  }

  private static async tryGetUserAliases(userIds: string[]): Promise<Record<string, string | undefined>> {
    const unique = Array.from(new Set((userIds || []).filter(Boolean)));
    if (unique.length === 0) return {};

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar, alias')
        .in('id', unique);

      if (error) throw error;

      const map: Record<string, string | undefined> = {};
      (data || []).forEach((u: any) => {
        if (u?.id) map[String(u.id)] = u.alias ? String(u.alias) : undefined;
      });
      return map;
    } catch {
      // Best-effort; RLS may prevent reading users table.
      return {};
    }

  }

  private static async getOrCreateProductInquiryConversation(receiverId: string, productId: string, subject: string): Promise<Conversation> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const canonicalSubject = await this.getCanonicalInquirySubject(productId, subject);

    const fetchExisting = async () => {
      const { data: conversation, error } = await supabase
        .from('conversations')
        .select(`
          *
        `)
        .or(`and(user1_id.eq.${user.id},user2_id.eq.${receiverId}),and(user1_id.eq.${receiverId},user2_id.eq.${user.id})`)
        .eq('product_id', productId)
        .is('offer_id', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return conversation as any;
    };

    const existing = await fetchExisting();
    if (existing) {
      // Best-effort repair: keep subject consistent with product
      try {
        const current = String((existing as any)?.subject || '').trim();
        if (canonicalSubject && current && current !== canonicalSubject) {
          await supabase
            .from('conversations')
            .update({ subject: canonicalSubject })
            .eq('id', (existing as any).id);
        }
      } catch {
        // ignore
      }

      const otherUserId = existing.user1_id === user.id ? existing.user2_id : existing.user1_id;
      return {
        ...existing,
        subject: canonicalSubject,
        other_user: {
          id: otherUserId,
          firstName: 'User',
          lastName: '',
          avatar: undefined,
          alias: undefined,
        },
        unread_count: 0,
      } as Conversation;
    }

    // Try inserting directly to guarantee correct product_id scoping.
    const payload: any = {
      user1_id: user.id,
      user2_id: receiverId,
      product_id: productId,
      offer_id: null,
      subject: canonicalSubject || null,
      last_message_at: new Date().toISOString(),
    };

    try {
      const { data: inserted, error: insertError } = await supabase
        .from('conversations')
        .insert(payload)
        .select(`
          *
        `)
        .single();
      if (insertError) throw insertError;

      const otherUserId = inserted.user1_id === user.id ? inserted.user2_id : inserted.user1_id;
      return {
        ...inserted,
        subject: canonicalSubject,
        other_user: {
          id: otherUserId,
          firstName: 'User',
          lastName: '',
          avatar: undefined,
          alias: undefined,
        },
        unread_count: 0,
      } as Conversation;
    } catch (e: any) {
      // If insert is blocked by RLS or race condition, fall back.
      try {
        const after = await fetchExisting();
        if (after) {
          const otherUserId = after.user1_id === user.id ? after.user2_id : after.user1_id;
          return {
            ...after,
            other_user: {
              id: otherUserId,
              firstName: 'User',
              lastName: '',
              avatar: undefined,
              alias: undefined,
            },
            unread_count: 0,
          } as Conversation;
        }
      } catch {}

      // Last resort: RPC, but validate it didn't return a conversation for the wrong product.
      const { data: rpcId, error: rpcErr } = await supabase
        .rpc('get_or_create_conversation', {
          p_user1_id: user.id,
          p_user2_id: receiverId,
          p_product_id: productId,
          p_subject: canonicalSubject || null,
          p_offer_id: null,
        });
      if (rpcErr) throw rpcErr;

      const { data: fetched, error: fetchErr } = await supabase
        .from('conversations')
        .select(`
          *
        `)
        .eq('id', rpcId)
        .single();
      if (fetchErr) throw fetchErr;

      const valid = String((fetched as any)?.product_id || '') === String(productId) && !(fetched as any)?.offer_id;
      if (!valid) {
        // Re-check for the correct one; if still missing, fail loudly.
        const finalTry = await fetchExisting();
        if (finalTry) {
          const otherUserId = finalTry.user1_id === user.id ? finalTry.user2_id : finalTry.user1_id;
          return {
            ...finalTry,
            other_user: {
              id: otherUserId,
              firstName: 'User',
              lastName: '',
              avatar: undefined,
              alias: undefined,
            },
            unread_count: 0,
          } as Conversation;
        }
        throw new Error('Conversation integrity error: backend returned conversation for a different product');
      }

      const otherUserId = fetched.user1_id === user.id ? fetched.user2_id : fetched.user1_id;
      return {
        ...fetched,
        subject: canonicalSubject,
        other_user: {
          id: otherUserId,
          firstName: 'User',
          lastName: '',
          avatar: undefined,
          alias: undefined,
        },
        unread_count: 0,
      } as Conversation;
    }
  }

  // Remove keys that are null or undefined (shallow compact)
  private static compactObject<T extends Record<string, any>>(input: T): Partial<T> {
    const result: Record<string, any> = {};
    Object.keys(input).forEach((key) => {
      const value = (input as any)[key];
      if (value !== null && value !== undefined) {
        result[key] = value;
      }
    });
    return result as Partial<T>;
  }

  // Append a message JSON into the per-user mirror table based on whether the
  // current user is user1 or user2 in the conversation. This function is best-effort
  // and failures should not block the primary send flow.
  private static async mirrorMessageToPerUserTables(conversationId: string, currentUserId: string, rawMessageRow: any) {
    try {
      // Only mirror messages WE sent
      if (rawMessageRow.sender_id !== currentUserId) return;

      // Find which side the current user is on
      const { data: conv } = await supabase
        .from('conversations')
        .select('id, user1_id, user2_id')
        .eq('id', conversationId)
        .single();
      if (!conv) return;

      const isUserOne = conv.user1_id === currentUserId;
      const tableName = isUserOne ? 'conversations_messages_user_one' : 'conversations_messages_user_two';

      // Ensure a row exists for this conversation (unique by conversation_id)
      await supabase
        .from(tableName)
        .upsert({ conversation_id: conversationId, user_id: currentUserId }, { onConflict: 'conversation_id' })
        .select('id')
        .single();

      // Fetch current messages array
      const { data: mirrorRow } = await supabase
        .from(tableName)
        .select('id, messages')
        .eq('conversation_id', conversationId)
        .single();

      // Build a compact JSON and strip nullish fields
      const messageJson: any = MessageService.compactObject({
        id: rawMessageRow.id,
        conversation_id: rawMessageRow.conversation_id,
        sender_id: rawMessageRow.sender_id,
        receiver_id: rawMessageRow.receiver_id,
        content: rawMessageRow.content,
        message_type: rawMessageRow.message_type,
        is_read: rawMessageRow.is_read,
        created_at: rawMessageRow.created_at,
        updated_at: rawMessageRow.updated_at,
        offer_id: rawMessageRow.offer_id,
        product_id: rawMessageRow.product_id,
        image_url: rawMessageRow.image_url,
        image_path: rawMessageRow.image_path,
        image_filename: rawMessageRow.image_filename,
        image_size: rawMessageRow.image_size,
        image_mime_type: rawMessageRow.image_mime_type,
        image_width: rawMessageRow.image_width,
        image_height: rawMessageRow.image_height,
        image_alt_text: rawMessageRow.image_alt_text,
        document_url: rawMessageRow.document_url,
        document_path: rawMessageRow.document_path,
        document_filename: rawMessageRow.document_filename,
        document_size: rawMessageRow.document_size,
        document_mime_type: rawMessageRow.document_mime_type,
        document_extension: rawMessageRow.document_extension,
        document_category: rawMessageRow.document_category,
        video_url: rawMessageRow.video_url,
        video_path: rawMessageRow.video_path,
        video_filename: rawMessageRow.video_filename,
        video_size: rawMessageRow.video_size,
        video_mime_type: rawMessageRow.video_mime_type,
        video_duration: rawMessageRow.video_duration,
        video_width: rawMessageRow.video_width,
        video_height: rawMessageRow.video_height,
        video_thumbnail_url: rawMessageRow.video_thumbnail_url,
        video_thumbnail_path: rawMessageRow.video_thumbnail_path,
      });

      // Avoid duplicates by id
      const existing = Array.isArray(mirrorRow?.messages)
        ? mirrorRow!.messages.some((m: any) => m?.id === messageJson.id)
        : false;
      if (existing) return;

      const nextMessages = Array.isArray(mirrorRow?.messages) ? [...mirrorRow!.messages, messageJson] : [messageJson];

      await supabase
        .from(tableName)
        .update({ messages: nextMessages, updated_at: new Date().toISOString() })
        .eq('conversation_id', conversationId);
    } catch (err) {
      console.warn('mirrorMessageToPerUserTables failed (non-fatal):', err);
    }
  }

  static onLocalMessage(callback: (message: Message) => void): () => void {
    this.localMessageListeners.push(callback);
    return () => {
      this.localMessageListeners = this.localMessageListeners.filter(cb => cb !== callback);
    };
  }

  private static emitLocalMessage(message: Message) {
    try {
      this.localMessageListeners.forEach(cb => cb(message));
    } catch (err) {
      console.warn('MessageService.emitLocalMessage listener error:', err);
    }
  }
  // Get all conversations for the current user
  static async getConversations(): Promise<Conversation[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('MessageService - Current user:', user);
      console.log('MessageService - User metadata:', user?.user_metadata);
      console.log('MessageService - User app metadata:', user?.app_metadata);
      
      if (!user) {
        console.error('MessageService - User not authenticated');
        throw new Error('User not authenticated');
      }

      console.log('MessageService - Fetching conversations for user:', user.id);
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *
        `)
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .order('last_message_at', { ascending: false });

      console.log('MessageService - Conversations query result:', { data, error });

      if (error) {
        const code = (error as any)?.code;
        if (code === '42703') {
          console.error('MessageService - Conversations query failed due to backend schema mismatch (likely a view/RLS policy referencing a non-existent column like products.title). Returning empty list as a fallback.', error);
          return [];
        }
        console.error('MessageService - Database error:', error);
        throw error;
      }

      console.log('MessageService - Raw conversation data:', data?.map(conv => ({
        id: conv.id,
        last_message_id: conv.last_message_id,
        last_message_at: conv.last_message_at
      })));

      // Fast path: avoid N+1 queries and heavy mirror-table reads.
      // We only need basic conversation list + other_user summary.
      const raw = (data || []) as any[];
      const otherUserIds = raw
        .map((conv) => (conv.user1_id === user.id ? conv.user2_id : conv.user1_id))
        .filter(Boolean);

      const aliasMap = await this.tryGetUserAliases(otherUserIds);

      return raw.map((conv) => {
        const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
        return {
          ...conv,
          other_user: {
            id: otherUserId,
            firstName: 'User',
            lastName: '',
            avatar: undefined,
            alias: aliasMap[String(otherUserId)],
          },
          unread_count: conv.unread_count ?? 0,
        } as Conversation;
      });
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  // Get messages for a specific conversation
  static async getMessages(conversationId: string, limit = 50, offset = 0): Promise<Message[]> {
    try {
      // Use the new conversation-based method
      return await this.getMessagesForConversation(conversationId, limit, offset);
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  // Get conversation by ID
  static async getConversationById(conversationId: string): Promise<Conversation | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (error) throw error;
      
      if (!data) return null;
      
      const otherUserId = data.user1_id === user.id ? data.user2_id : data.user1_id;
      
      // Fetch user details for the other user from the users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar, alias')
        .eq('id', otherUserId)
        .single();
      
      console.log('getConversationById - User data for', otherUserId, ':', userData, 'Error:', userError);
      
      return {
        ...data,
        other_user: {
          id: otherUserId,
          firstName: userData?.first_name || 'User',
          lastName: userData?.last_name || '',
          avatar: userData?.avatar,
          alias: userData?.alias,
        },
        unread_count: 0
      };
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return null;
    }
  }

  // Get messages between two users
  static async getMessagesForConversation(conversationId: string, limit = 50, offset = 0): Promise<Message[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Determine which per-user table holds OUR sent messages for this conversation
      const { data: conv, error: convErr } = await supabase
        .from('conversations')
        .select('id, user1_id, user2_id')
        .eq('id', conversationId)
        .single();
      if (convErr) throw convErr;
      const isUserOne = conv?.user1_id === user.id;
      const tableName = isUserOne ? 'conversations_messages_user_one' : 'conversations_messages_user_two';
      const otherUserId = isUserOne ? conv?.user2_id : conv?.user1_id;
      const otherTableName = isUserOne ? 'conversations_messages_user_two' : 'conversations_messages_user_one';

      // Read our pre-aggregated messages JSON for this conversation
      const { data: mirrorRow, error: mirrorErr } = await supabase
        .from(tableName)
        .select('messages')
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .single();

      // Best-effort read of the other mirror table (may be blocked by RLS)
      const { data: otherMirrorRow, error: otherMirrorErr } = await supabase
        .from(otherTableName)
        .select('messages')
        .eq('conversation_id', conversationId)
        .single();

      if (mirrorErr && mirrorErr.code !== 'PGRST116') {
        // If other error (not RLS/no row), surface it; otherwise fall back to empty
        console.warn('Mirror messages load error:', mirrorErr);
      }

      if (otherMirrorErr && otherMirrorErr.code !== 'PGRST116') {
        console.warn('Other mirror messages load error:', otherMirrorErr);
      }

      const jsonMessagesA: any[] = Array.isArray(mirrorRow?.messages) ? mirrorRow!.messages : [];
      const jsonMessagesB: any[] = Array.isArray((otherMirrorRow as any)?.messages) ? (otherMirrorRow as any).messages : [];

      // Merge + dedupe by message id
      const byId = new Map<string, any>();
      [...jsonMessagesA, ...jsonMessagesB].forEach((m: any) => {
        if (m && typeof m.id === 'string') byId.set(m.id, m);
      });
      const mergedAll = Array.from(byId.values())
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      // Slice/paginate from the end (messages are stored in chronological append order)
      const start = Math.max(0, mergedAll.length - (offset + limit));
      const end = Math.max(0, mergedAll.length - offset);
      const page = mergedAll.slice(start, end);

      // Enrich sender/receiver once
      const [{ data: senderData }, { data: receiverData }] = await Promise.all([
        supabase.from('users').select('id, first_name, last_name, avatar, alias').eq('id', user.id).single(),
        supabase.from('users').select('id, first_name, last_name, avatar, alias').eq('id', otherUserId).single()
      ]);

      const messages: Message[] = page
        .map((msg: any) => ({
          id: msg.id,
          sender_id: msg.sender_id,
          receiver_id: msg.receiver_id,
          conversation_id: msg.conversation_id,
          content: msg.content,
          message_type: msg.message_type,
          is_read: msg.is_read,
          created_at: msg.created_at,
          updated_at: msg.updated_at,
          offer_id: msg.offer_id ?? undefined,
          product_id: msg.product_id ?? undefined,
          image_url: msg.image_url ?? undefined,
          image_path: msg.image_path ?? undefined,
          image_filename: msg.image_filename ?? undefined,
          image_size: msg.image_size ?? undefined,
          image_mime_type: msg.image_mime_type ?? undefined,
          image_width: msg.image_width ?? undefined,
          image_height: msg.image_height ?? undefined,
          image_alt_text: msg.image_alt_text ?? undefined,
          document_url: msg.document_url ?? undefined,
          document_path: msg.document_path ?? undefined,
          document_filename: msg.document_filename ?? undefined,
          document_size: msg.document_size ?? undefined,
          document_mime_type: msg.document_mime_type ?? undefined,
          document_extension: msg.document_extension ?? undefined,
          document_category: msg.document_category ?? undefined,
          video_url: msg.video_url ?? undefined,
          video_path: msg.video_path ?? undefined,
          video_filename: msg.video_filename ?? undefined,
          video_size: msg.video_size ?? undefined,
          video_mime_type: msg.video_mime_type ?? undefined,
          video_duration: msg.video_duration ?? undefined,
          video_width: msg.video_width ?? undefined,
          video_height: msg.video_height ?? undefined,
          video_thumbnail_url: msg.video_thumbnail_url ?? undefined,
          video_thumbnail_path: msg.video_thumbnail_path ?? undefined,
          sender: msg.sender_id === user.id ? {
            id: user.id,
            firstName: senderData?.first_name || 'User',
            lastName: senderData?.last_name || '',
            avatar: senderData?.avatar,
            alias: senderData?.alias,
          } : {
            id: otherUserId,
            firstName: receiverData?.first_name || 'User',
            lastName: receiverData?.last_name || '',
            avatar: receiverData?.avatar,
            alias: receiverData?.alias,
          },
          receiver: msg.sender_id === user.id ? {
            id: otherUserId,
            firstName: receiverData?.first_name || 'User',
            lastName: receiverData?.last_name || '',
            avatar: receiverData?.avatar,
            alias: receiverData?.alias,
          } : {
            id: user.id,
            firstName: senderData?.first_name || 'User',
            lastName: senderData?.last_name || '',
            avatar: senderData?.avatar,
            alias: senderData?.alias,
          }
        }));

      return messages;
    } catch (error) {
      console.error('Error fetching messages between users:', error);
      throw error;
    }
  }

  // Backward-compatible method for getting messages between users
  // This method gets or creates a conversation and then loads messages for that conversation
  static async getMessagesBetweenUsers(user1Id: string, user2Id: string, limit = 50, offset = 0): Promise<Message[]> {
    try {
      // Get or create conversation between the two users with retry logic
      let conversationId: string | undefined;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          const { data, error: convError } = await supabase
            .rpc('get_or_create_conversation', {
              p_user1_id: user1Id,
              p_user2_id: user2Id,
              p_product_id: null,
              p_subject: null
            });

          if (convError) {
            // If it's a unique constraint violation, retry
            if (convError.code === '23505' && retryCount < maxRetries - 1) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 100 * retryCount)); // Exponential backoff
              continue;
            }
            throw convError;
          }
          
          conversationId = data;
          break;
        } catch (error: any) {
          // If it's a unique constraint violation, retry
          if (error.code === '23505' && retryCount < maxRetries - 1) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, 100 * retryCount)); // Exponential backoff
            continue;
          }
          throw error;
        }
      }
      
      if (!conversationId) {
        throw new Error('Failed to get or create conversation after retries');
      }

      // Load messages for the conversation
      return await this.getMessagesForConversation(conversationId, limit, offset);
    } catch (error) {
      console.error('Error getting messages between users:', error);
      throw error;
    }
  }

  // Send a message (now writes directly into per-user mirror table)
  static async sendMessage(receiverId: string, content: string, messageType: 'text' | 'image' | 'file' | 'offer' | 'video' | 'document' = 'text', productId?: string, subject?: string, extra?: { offerId?: string; imageUrl?: string; imagePath?: string; imageFilename?: string; imageSize?: number; imageMimeType?: string; imageWidth?: number; imageHeight?: number; imageAltText?: string; videoUrl?: string; videoPath?: string; videoFilename?: string; videoSize?: number; videoMimeType?: string; videoDuration?: number; videoWidth?: number; videoHeight?: number; videoThumbnailUrl?: string; videoThumbnailPath?: string; documentUrl?: string; documentPath?: string; documentFilename?: string; documentSize?: number; documentMimeType?: string; documentExtension?: string; documentCategory?: string }, conversationId?: string): Promise<Message> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let finalConversationId: string | undefined = conversationId;

      // If no conversationId is provided, get or create conversation with retry logic for race conditions
      if (!finalConversationId) {
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            const { data, error: convError } = await supabase
              .rpc('get_or_create_conversation', {
                p_user1_id: user.id,
                p_user2_id: receiverId,
                p_product_id: productId || null,
                p_subject: subject || null,
                p_offer_id: extra?.offerId || null
              });

            if (convError) {
              // If it's a unique constraint violation, retry
              if (convError.code === '23505' && retryCount < maxRetries - 1) {
                retryCount++;
                await new Promise(resolve => setTimeout(resolve, 100 * retryCount)); // Exponential backoff
                continue;
              }
              throw convError;
            }
            
            finalConversationId = data;
            break;
          } catch (error: any) {
            // If it's a unique constraint violation, retry
            if (error.code === '23505' && retryCount < maxRetries - 1) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 100 * retryCount)); // Exponential backoff
              continue;
            }
            throw error;
          }
        }
        
        if (!finalConversationId) {
          throw new Error('Failed to get or create conversation after retries');
        }
      }

      // Determine which per-user table to write to
      const { data: conv, error: convErr } = await supabase
        .from('conversations')
        .select('id, user1_id, user2_id')
        .eq('id', finalConversationId)
        .single();
      if (convErr) throw convErr;
      const isUserOne = conv?.user1_id === user.id;
      const tableName = isUserOne ? 'conversations_messages_user_one' : 'conversations_messages_user_two';
      const otherTableName = isUserOne ? 'conversations_messages_user_two' : 'conversations_messages_user_one';
      const otherUserId = isUserOne ? conv?.user2_id : conv?.user1_id;

      // Ensure a row exists for this conversation+user
      await supabase
        .from(tableName)
        .upsert({ conversation_id: finalConversationId, user_id: user.id }, { onConflict: 'conversation_id' })
        .select('id')
        .single();

      // Fetch current messages JSON
      const { data: mirrorRow } = await supabase
        .from(tableName)
        .select('id, messages, number_messages')
        .eq('conversation_id', finalConversationId)
        .single();

      // Build the new message JSON
      const messageId = (globalThis as any).crypto?.randomUUID ? (globalThis as any).crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const createdAt = new Date().toISOString();
      const rawMessageRow: any = MessageService.compactObject({
        id: messageId,
        sender_id: user.id,
        receiver_id: receiverId,
        conversation_id: finalConversationId,
        content,
        message_type: messageType,
        is_read: false,
        created_at: createdAt,
        updated_at: createdAt,
        offer_id: extra?.offerId,
        product_id: productId,
        // Image fields
        image_url: extra?.imageUrl,
        image_path: extra?.imagePath,
        image_filename: extra?.imageFilename,
        image_size: extra?.imageSize,
        image_mime_type: extra?.imageMimeType,
        image_width: extra?.imageWidth,
        image_height: extra?.imageHeight,
        image_alt_text: extra?.imageAltText,
        // Video fields
        video_url: extra?.videoUrl,
        video_path: extra?.videoPath,
        video_filename: extra?.videoFilename,
        video_size: extra?.videoSize,
        video_mime_type: extra?.videoMimeType,
        video_duration: extra?.videoDuration,
        video_width: extra?.videoWidth,
        video_height: extra?.videoHeight,
        video_thumbnail_url: extra?.videoThumbnailUrl,
        video_thumbnail_path: extra?.videoThumbnailPath,
        // Document fields
        document_url: extra?.documentUrl,
        document_path: extra?.documentPath,
        document_filename: extra?.documentFilename,
        document_size: extra?.documentSize,
        document_mime_type: extra?.documentMimeType,
        document_extension: extra?.documentExtension,
        document_category: extra?.documentCategory,
      });

      const nextMessages = Array.isArray(mirrorRow?.messages)
        ? [...mirrorRow!.messages, rawMessageRow]
        : [rawMessageRow];

      let updateErr: any = null;
      try {
        const { error } = await supabase
          .from(tableName)
          .update({ messages: nextMessages, number_messages: nextMessages.length, updated_at: createdAt } as any)
          .eq('conversation_id', finalConversationId);
        updateErr = error;
      } catch (e) {
        updateErr = e;
      }

      if (updateErr) {
        const { error } = await supabase
          .from(tableName)
          .update({ messages: nextMessages, updated_at: createdAt } as any)
          .eq('conversation_id', finalConversationId);
        updateErr = error;
      }

      if (updateErr) throw updateErr;

      try {
        if (otherUserId) {
          await supabase
            .from(otherTableName)
            .upsert({ conversation_id: finalConversationId, user_id: otherUserId }, { onConflict: 'conversation_id' })
            .select('id')
            .single();

          const { data: otherMirrorRow } = await supabase
            .from(otherTableName)
            .select('id, messages, number_messages')
            .eq('conversation_id', finalConversationId)
            .single();

          const otherExisting = Array.isArray(otherMirrorRow?.messages)
            ? otherMirrorRow!.messages.some((m: any) => m?.id === rawMessageRow.id)
            : false;
          if (!otherExisting) {
            const otherNextMessages = Array.isArray(otherMirrorRow?.messages)
              ? [...otherMirrorRow!.messages, rawMessageRow]
              : [rawMessageRow];

            try {
              const { error: otherUpdateErr } = await supabase
                .from(otherTableName)
                .update({ messages: otherNextMessages, number_messages: otherNextMessages.length, updated_at: createdAt } as any)
                .eq('conversation_id', finalConversationId);
              if (otherUpdateErr) {
                await supabase
                  .from(otherTableName)
                  .update({ messages: otherNextMessages, updated_at: createdAt } as any)
                  .eq('conversation_id', finalConversationId);
              }
            } catch {
              await supabase
                .from(otherTableName)
                .update({ messages: otherNextMessages, updated_at: createdAt } as any)
                .eq('conversation_id', finalConversationId);
            }
          }
        }
      } catch (err) {
        console.warn('sendMessage - mirroring to other table failed (non-fatal):', err);
      }

      try {
        await supabase
          .from('conversations')
          .update({ last_message_id: messageId, last_message_at: createdAt, updated_at: createdAt })
          .eq('id', finalConversationId);
      } catch (err) {
        console.warn('sendMessage - failed to update conversations last_message fields (non-fatal):', err);
      }
      
      // Fetch sender and receiver details
      const { data: senderData } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar, alias')
        .eq('id', rawMessageRow.sender_id)
        .single();
      
      const { data: receiverData } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar, alias')
        .eq('id', rawMessageRow.receiver_id)
        .single();
      
      // Transform data to include sender and receiver info
      const message: Message = {
        ...rawMessageRow,
        sender: {
          id: rawMessageRow.sender_id,
          firstName: senderData?.first_name || 'User',
          lastName: senderData?.last_name || '',
          avatar: senderData?.avatar,
          alias: senderData?.alias,
        },
        receiver: {
          id: rawMessageRow.receiver_id,
          firstName: receiverData?.first_name || 'User',
          lastName: receiverData?.last_name || '',
          avatar: receiverData?.avatar,
          alias: receiverData?.alias,
        }
      };

      // Immediately broadcast locally so UIs can update without waiting for realtime
      MessageService.emitLocalMessage(message);
      return message;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Send a rich offer message linked to an offer and product
  static async sendOfferMessage(
    receiverId: string,
    productId: string,
    offerId: string,
    offerPrice: number,
    offerText: string,
    subject?: string,
    conversationId?: string
  ): Promise<Message> {
    const content = `NEW_OFFER|price=${offerPrice}|text=${offerText}`;
    // Try proper 'offer' type; if it fails for any reason, fall back to 'text'
    try {
      return await this.sendMessage(receiverId, content, 'offer', productId, subject, { offerId }, conversationId);
    } catch (firstErr: any) {
      try {
        return await this.sendMessage(receiverId, content, 'text', productId, subject, { offerId }, conversationId);
      } catch (secondErr) {
        // Surface the original error, but include fallback attempt info
        const original = firstErr?.message || String(firstErr);
        const fallback = (secondErr as any)?.message || String(secondErr);
        throw new Error(`Offer send failed. Original: ${original}. Fallback as text also failed: ${fallback}`);
      }
    }
  }

  // Check if conversation already exists for specific product
  static async getConversationForProduct(
    receiverId: string, 
    productId: string
  ): Promise<Conversation | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Look for existing conversation with this product
      const { data: conversation, error } = await supabase
        .from('conversations')
        .select(`
          *,
          product:products(id, name, price, images)
        `)
        .or(`and(user1_id.eq.${user.id},user2_id.eq.${receiverId}),and(user1_id.eq.${receiverId},user2_id.eq.${user.id})`)
        .eq('product_id', productId)
        .is('offer_id', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No conversation found
          return null;
        }
        throw error;
      }

      // Get other user details
      const otherUserId = conversation.user1_id === user.id ? conversation.user2_id : conversation.user1_id;
      const { data: otherUserData } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar, alias')
        .eq('id', otherUserId)
        .single();

      // Transform to Conversation interface
      const conversationData: Conversation = {
        ...conversation,
        other_user: {
          id: otherUserId,
          firstName: otherUserData?.first_name || 'User',
          lastName: otherUserData?.last_name || '',
          avatar: otherUserData?.avatar,
          alias: otherUserData?.alias,
        },
        product: conversation.product ? {
          id: conversation.product.id,
          name: conversation.product.name,
          price: conversation.product.price,
          images: conversation.product.images || []
        } : undefined
      };

      return conversationData;
    } catch (error) {
      console.error('Error getting conversation for product:', error);
      return null;
    }
  }

  // Create or get conversation with product details
  // If offerId is provided, always creates a NEW conversation (one conversation per offer)
  // If offerId is NOT provided, gets existing conversation or creates new one (one conversation per product)
  static async createConversationWithProduct(
    receiverId: string, 
    productId: string, 
    subject: string,
    offerId?: string
  ): Promise<Conversation> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // If offerId is provided, always create a NEW conversation (don't check for existing)
      // This ensures each offer gets its own conversation entry
      if (offerId) {
        console.log('Creating NEW conversation for offer:', offerId);
        // Always create new conversation for offers
        const { data: conversationId, error: convError } = await supabase
          .rpc('get_or_create_conversation', {
            p_user1_id: user.id,
            p_user2_id: receiverId,
            p_product_id: productId,
            p_subject: subject,
            p_offer_id: offerId
          });

        if (convError) throw convError;

        // Fetch the conversation with product details
        const { data: conversation, error: fetchError } = await supabase
          .from('conversations')
          .select(`
            *,
            product:products(id, name, price, images)
          `)
          .eq('id', conversationId)
          .single();

        if (fetchError) throw fetchError;

        // Get other user details
        const otherUserId = conversation.user1_id === user.id ? conversation.user2_id : conversation.user1_id;
        const { data: otherUserData, error: userError } = await supabase
          .from('users')
          .select('id, first_name, last_name, avatar, alias')
          .eq('id', otherUserId)
          .single();

        // Handle RLS errors by trying alternative approach
        let finalUserData = otherUserData;
        if (userError && userError.code === 'PGRST116') {
          console.log('MessageService - RLS error detected, trying alternative query...');
          
          // Try to get user data using a function that bypasses RLS
          const { data: functionData, error: functionError } = await supabase
            .rpc('get_user_alias', { user_id: otherUserId });
          
          if (functionData && !functionError) {
            // Create a minimal user data object with the alias
            finalUserData = {
              id: otherUserId,
              first_name: 'User',
              last_name: '',
              alias: functionData,
              avatar: null,
            };
          }
        }

        // Transform to Conversation interface
        const conversationData: Conversation = {
          ...conversation,
          other_user: {
            id: otherUserId,
            firstName: finalUserData?.first_name || 'User',
            lastName: finalUserData?.last_name || '',
            avatar: finalUserData?.avatar,
            alias: finalUserData?.alias,
          },
          unread_count: 0,
        };

        return conversationData;
      }

      // If no offerId, check for existing conversation first (for regular product inquiries)
      return await this.getOrCreateProductInquiryConversation(receiverId, productId, subject);
    } catch (error) {
      console.error('Error creating conversation with product:', error);
      throw error;
    }
  }

  // Mark messages as read
  static async markMessagesAsRead(senderId: string, receiverId: string): Promise<void> {
    try {
      const { error } = await supabase
        .rpc('mark_messages_as_read', {
          p_sender_id: senderId,
          p_receiver_id: receiverId
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  }

  static async markConversationMessagesAsRead(conversationId: string, currentUserId: string): Promise<void> {
    // No-op with mirror JSON model (reading state is implicit per-user)
    return;
  }

  // Get unread message count for a conversation
  static async getUnreadCount(conversationId: string, currentUserId: string): Promise<number> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      const { data: conv } = await supabase
        .from('conversations')
        .select('id, user1_id, user2_id')
        .eq('id', conversationId)
        .single();
      if (!conv) return 0;
      const isUserOne = conv.user1_id === user.id;
      const tableName = isUserOne ? 'conversations_messages_user_one' : 'conversations_messages_user_two';
      const otherTableName = isUserOne ? 'conversations_messages_user_two' : 'conversations_messages_user_one';

      const [ownSide, otherSide] = await Promise.all([
        supabase
          .from(tableName)
          .select('messages')
          .eq('conversation_id', conversationId)
          .eq('user_id', user.id)
          .single(),
        supabase
          .from(otherTableName)
          .select('messages')
          .eq('conversation_id', conversationId)
          .single(),
      ]);

      const ownMsgs: any[] = Array.isArray((ownSide as any)?.data?.messages) ? (ownSide as any).data.messages : [];
      const otherMsgs: any[] = Array.isArray((otherSide as any)?.data?.messages) ? (otherSide as any).data.messages : [];

      const mergedById = new Map<string, any>();
      [...ownMsgs, ...otherMsgs].forEach((m: any) => {
        if (m && typeof m.id === 'string') mergedById.set(m.id, m);
      });
      const merged = Array.from(mergedById.values());

      // Count messages not sent by me and not marked read (defaults to false/null)
      return merged.filter(m => m?.sender_id !== currentUserId && !m?.is_read).length;
    } catch {
      return 0;
    }
  }

  // Subscribe to new messages between two users
  static async subscribeToMessages(user1Id: string, user2Id: string, callback: (message: Message) => void) {
    // Backward compatibility - get conversation and use conversation-based subscription
    try {
      // Get or create conversation with retry logic
      let conversationId: string | undefined;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          const { data, error: convError } = await supabase
            .rpc('get_or_create_conversation', {
              p_user1_id: user1Id,
              p_user2_id: user2Id,
              p_product_id: null,
              p_subject: null
            });

          if (convError) {
            // If it's a unique constraint violation, retry
            if (convError.code === '23505' && retryCount < maxRetries - 1) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 100 * retryCount)); // Exponential backoff
              continue;
            }
            throw convError;
          }
          
          conversationId = data;
          break;
        } catch (error: any) {
          // If it's a unique constraint violation, retry
          if (error.code === '23505' && retryCount < maxRetries - 1) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, 100 * retryCount)); // Exponential backoff
            continue;
          }
          throw error;
        }
      }
      
      if (!conversationId) {
        throw new Error('Failed to get or create conversation after retries');
      }
      
      return this.subscribeToConversationMessages(conversationId, callback);
    } catch (error) {
      console.error('Error getting conversation for subscription:', error);
      return null;
    }
  }

  static async subscribeToConversationMessages(conversationId: string, callback: (message: Message) => void) {
    // We listen to two sources in parallel:
    // 1) Legacy base table `messages` INSERTs (images/documents/videos still use it)
    // 2) Per-user mirror table UPDATEs on the messages JSON array (new model)

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Determine which mirror table this user writes to for this conversation
    const { data: conv } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id')
      .eq('id', conversationId)
      .single();

    const isUserOne = conv?.user1_id === user.id;
    const tableName = isUserOne ? 'conversations_messages_user_one' : 'conversations_messages_user_two';
    const otherUserId = isUserOne ? conv?.user2_id : conv?.user1_id;
    const otherTableName = isUserOne ? 'conversations_messages_user_two' : 'conversations_messages_user_one';

    const channel = supabase
      .channel(`messages:conversation:${conversationId}`)
      // Removed legacy base table listener (messages table no longer exists)
      // Mirror table UPDATEs (messages JSON appended)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: tableName, filter: `conversation_id=eq.${conversationId}&user_id=eq.${user.id}` },
        async (payload) => {
          const row: any = payload.new;
          const messagesJson: any[] = Array.isArray(row?.messages) ? row.messages : [];
          if (messagesJson.length === 0) return;
          const last = messagesJson[messagesJson.length - 1];

          const receiverId = last?.receiver_id
            ?? (last?.sender_id === user.id ? otherUserId : user.id);

          const [{ data: senderData }, { data: receiverData }] = await Promise.all([
            supabase.from('users').select('id, first_name, last_name, avatar, alias').eq('id', last.sender_id).single(),
            supabase.from('users').select('id, first_name, last_name, avatar, alias').eq('id', receiverId).single()
          ]);

          const transformedMessage: Message = {
            id: last.id,
            sender_id: last.sender_id,
            receiver_id: receiverId,
            conversation_id: last.conversation_id ?? conversationId,
            content: last.content,
            message_type: last.message_type,
            is_read: !!last.is_read,
            created_at: last.created_at,
            updated_at: last.updated_at ?? last.created_at,
            offer_id: last.offer_id ?? undefined,
            product_id: last.product_id ?? undefined,
            image_url: last.image_url ?? undefined,
            image_path: last.image_path ?? undefined,
            image_filename: last.image_filename ?? undefined,
            image_size: last.image_size ?? undefined,
            image_mime_type: last.image_mime_type ?? undefined,
            image_width: last.image_width ?? undefined,
            image_height: last.image_height ?? undefined,
            image_alt_text: last.image_alt_text ?? undefined,
            document_url: last.document_url ?? undefined,
            document_path: last.document_path ?? undefined,
            document_filename: last.document_filename ?? undefined,
            document_size: last.document_size ?? undefined,
            document_mime_type: last.document_mime_type ?? undefined,
            document_extension: last.document_extension ?? undefined,
            document_category: last.document_category ?? undefined,
            video_url: last.video_url ?? undefined,
            video_path: last.video_path ?? undefined,
            video_filename: last.video_filename ?? undefined,
            video_size: last.video_size ?? undefined,
            video_mime_type: last.video_mime_type ?? undefined,
            video_duration: last.video_duration ?? undefined,
            video_width: last.video_width ?? undefined,
            video_height: last.video_height ?? undefined,
            video_thumbnail_url: last.video_thumbnail_url ?? undefined,
            video_thumbnail_path: last.video_thumbnail_path ?? undefined,
            sender: {
              id: last.sender_id,
              firstName: senderData?.first_name || 'User',
              lastName: senderData?.last_name || '',
              avatar: senderData?.avatar,
              alias: senderData?.alias,
            },
            receiver: {
              id: receiverId,
              firstName: receiverData?.first_name || 'User',
              lastName: receiverData?.last_name || '',
              avatar: receiverData?.avatar,
              alias: receiverData?.alias,
            }
          };
          callback(transformedMessage);
        }
      )
      // Also listen to the partner mirror table updates so chats update for both sides
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: otherTableName, filter: `conversation_id=eq.${conversationId}` },
        async (payload) => {
          const row: any = payload.new;
          const messagesJson: any[] = Array.isArray(row?.messages) ? row.messages : [];
          if (messagesJson.length === 0) return;
          const last = messagesJson[messagesJson.length - 1];

          const receiverId = last?.receiver_id
            ?? (last?.sender_id === user.id ? otherUserId : user.id);

          const [{ data: senderData }, { data: receiverData }] = await Promise.all([
            supabase.from('users').select('id, first_name, last_name, avatar, alias').eq('id', last.sender_id).single(),
            supabase.from('users').select('id, first_name, last_name, avatar, alias').eq('id', receiverId).single()
          ]);

          const transformedMessage: Message = {
            id: last.id,
            sender_id: last.sender_id,
            receiver_id: receiverId,
            conversation_id: last.conversation_id ?? conversationId,
            content: last.content,
            message_type: last.message_type,
            is_read: !!last.is_read,
            created_at: last.created_at,
            updated_at: last.updated_at ?? last.created_at,
            offer_id: last.offer_id ?? undefined,
            product_id: last.product_id ?? undefined,
            image_url: last.image_url ?? undefined,
            image_path: last.image_path ?? undefined,
            image_filename: last.image_filename ?? undefined,
            image_size: last.image_size ?? undefined,
            image_mime_type: last.image_mime_type ?? undefined,
            image_width: last.image_width ?? undefined,
            image_height: last.image_height ?? undefined,
            image_alt_text: last.image_alt_text ?? undefined,
            document_url: last.document_url ?? undefined,
            document_path: last.document_path ?? undefined,
            document_filename: last.document_filename ?? undefined,
            document_size: last.document_size ?? undefined,
            document_mime_type: last.document_mime_type ?? undefined,
            document_extension: last.document_extension ?? undefined,
            document_category: last.document_category ?? undefined,
            video_url: last.video_url ?? undefined,
            video_path: last.video_path ?? undefined,
            video_filename: last.video_filename ?? undefined,
            video_size: last.video_size ?? undefined,
            video_mime_type: last.video_mime_type ?? undefined,
            video_duration: last.video_duration ?? undefined,
            video_width: last.video_width ?? undefined,
            video_height: last.video_height ?? undefined,
            video_thumbnail_url: last.video_thumbnail_url ?? undefined,
            video_thumbnail_path: last.video_thumbnail_path ?? undefined,
            sender: {
              id: last.sender_id,
              firstName: senderData?.first_name || 'User',
              lastName: senderData?.last_name || '',
              avatar: senderData?.avatar,
              alias: senderData?.alias,
            },
            receiver: {
              id: receiverId,
              firstName: receiverData?.first_name || 'User',
              lastName: receiverData?.last_name || '',
              avatar: receiverData?.avatar,
              alias: receiverData?.alias,
            }
          };

          callback(transformedMessage);
        }
      )
      .subscribe();

    return channel;
  }

  // Subscribe to conversation updates
  static async subscribeToConversations(callback: (conversation: Conversation) => void) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    return supabase
      .channel(`conversations:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `or(user1_id.eq.${user.id},user2_id.eq.${user.id})`
        },
        async (payload) => {
          const conversation = payload.new as any;
          const otherUserId = conversation.user1_id === user.id ? conversation.user2_id : conversation.user1_id;
          
          // Fetch user details for the other user
          const { data: userData } = await supabase
            .from('users')
            .select('id, first_name, last_name, avatar, alias')
            .eq('id', otherUserId)
            .single();
          
          // Transform conversation to include other_user info
          const transformedConversation: Conversation = {
            ...conversation,
            other_user: {
              id: otherUserId,
              firstName: userData?.first_name || 'User',
              lastName: userData?.last_name || '',
              avatar: userData?.avatar,
              alias: userData?.alias,
            },
            unread_count: 0
          };
          
          callback(transformedConversation);
        }
      )
      .subscribe();
  }

  // Add user to active users list for a conversation
  static async addUserToConversation(conversationId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('add_user_to_conversation', {
        p_conversation_id: conversationId,
        p_user_id: userId
      });

      if (error) {
        console.error('Error adding user to conversation:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in addUserToConversation:', error);
      throw error;
    }
  }

  // Remove user from active users list for a conversation
  static async removeUserFromConversation(conversationId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('remove_user_from_conversation', {
        p_conversation_id: conversationId,
        p_user_id: userId
      });

      if (error) {
        console.error('Error removing user from conversation:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in removeUserFromConversation:', error);
      throw error;
    }
  }

  // Get active users for a conversation
  static async getConversationActiveUsers(conversationId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase.rpc('get_conversation_active_users', {
        p_conversation_id: conversationId
      });

      if (error) {
        console.error('Error getting conversation active users:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getConversationActiveUsers:', error);
      throw error;
    }
  }
}
